import { useEffect, useMemo, useRef, useState } from 'react';
import { drag } from 'd3-drag';
import {
  forceCenter,
  forceCollide,
  forceLink,
  forceManyBody,
  forceSimulation,
  type Simulation,
  type SimulationNodeDatum,
} from 'd3-force';
import { select, type Selection } from 'd3-selection';
import { zoom, zoomIdentity, type ZoomBehavior } from 'd3-zoom';
import type { NetworkGraph as NetworkGraphData } from '@marstein-radar/shared';

// Interaktívny graf prepojení osôb↔firiem (Blok 1, Týždeň 6). d3-force robí layout,
// samotné SVG kreslíme/aktualizujeme priamo cez d3-selection (nie React state per tick
// — simulácia tiká desiatky-krát za sekundu, re-render celého Reactu by bol zbytočne
// drahý). React tu len drží kontajner + spúšťa/ruší simuláciu pri zmene dát.

interface SimNode extends SimulationNodeDatum {
  id: string;
  type: 'company' | 'person';
  label: string;
  ico?: string;
  degree: number;
  identityConfirmed?: boolean;
}
interface SimLink {
  source: string | SimNode;
  target: string | SimNode;
  roleLabel: string;
  roleCategory: string;
  historical: boolean;
}

const ROLE_COLOR: Record<string, string> = {
  statutar: 'var(--brand)',
  organ: 'var(--slate-light)',
  ubo: 'var(--risk-mid)',
};

const HEIGHT = 520;

export function NetworkGraph({
  graph,
  showHistorical,
  onSelectNode,
}: {
  graph: NetworkGraphData;
  showHistorical: boolean;
  onSelectNode?: (nodeId: string) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const zoomRef = useRef<ZoomBehavior<SVGSVGElement, unknown> | null>(null);
  const simulationRef = useRef<Simulation<SimNode, SimLink> | null>(null);
  const nodeSelRef = useRef<Selection<SVGGElement, SimNode, SVGGElement, unknown> | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const { nodes, links } = useMemo(() => {
    const simLinks: SimLink[] = graph.edges
      .filter((e) => showHistorical || !e.validTo)
      .map((e) => ({
        source: e.from,
        target: e.to,
        roleLabel: e.roleLabel,
        roleCategory: e.roleCategory,
        historical: !!e.validTo,
      }));
    // Stupeň uzla = počet UNIKÁTNYCH susedov (nie hrán — osoba môže mať k tej istej
    // firme 2 záznamy, RPO rola + RPVS UBO, čo by inak falošne vyzeralo ako 2 väzby).
    // Vizuálne zvýrazňuje "huby": osoby pôsobiace vo viacerých firmách naraz sú
    // okamžite viditeľné väčším a zvýrazneným uzlom (investigatívny use case — "kde
    // všade táto osoba pôsobí").
    const neighbors = new Map<string, Set<string>>();
    for (const l of simLinks) {
      const s = l.source as string;
      const t = l.target as string;
      if (!neighbors.has(s)) neighbors.set(s, new Set());
      if (!neighbors.has(t)) neighbors.set(t, new Set());
      neighbors.get(s)!.add(t);
      neighbors.get(t)!.add(s);
    }
    const simNodes: SimNode[] = graph.nodes.map((n) => ({
      ...n,
      degree: neighbors.get(n.id)?.size ?? 0,
    }));
    return { nodes: simNodes, links: simLinks };
  }, [graph, showHistorical]);

  useEffect(() => {
    const svgEl = svgRef.current;
    const container = containerRef.current;
    if (!svgEl || !container) return;

    const width = container.clientWidth || 800;
    const height = HEIGHT;

    const svg = select(svgEl).attr('viewBox', `0 0 ${width} ${height}`);
    svg.selectAll('*').remove();

    const viewport = svg.append('g').attr('class', 'viewport');
    const linkLayer = viewport.append('g').attr('class', 'links');
    const nodeLayer = viewport.append('g').attr('class', 'nodes');

    const radius = (d: SimNode): number => {
      const base = d.type === 'company' ? 14 : 9;
      return Math.min(base + Math.sqrt(Math.max(0, d.degree - 1)) * 5, base + 16);
    };
    // "Hub" osoba — má rolu vo viacerých firmách naraz v tomto grafe (degree > 1
    // znamená minimálne 2 väzby, teda min. 2 firmy). Zvýraznené oranžovou (--risk-mid),
    // aby vyskočili okamžite z grafu.
    const isHub = (d: SimNode): boolean => d.type === 'person' && d.degree > 1;
    // Osoba bez dátumu narodenia v zdrojových dátach — dedup kľúč je len meno
    // (persons/fingerprint.ts), takže tento uzol môže v skutočnosti byť 2+ rôznych
    // ľudí s rovnakým menom zlúčených do jedného ("Martin Šuster" naprieč dvoma
    // neprepojenými firmami bol reálny prípad, ktorý toto odhalilo). Prerušovaný
    // obrys namiesto tichého tvrdenia istoty.
    const isUnconfirmed = (d: SimNode): boolean => d.type === 'person' && !d.identityConfirmed;

    const simulation = forceSimulation<SimNode>(nodes)
      .force(
        'link',
        forceLink<SimNode, SimLink>(links)
          .id((d) => d.id)
          .distance(95),
      )
      .force('charge', forceManyBody().strength(-240))
      .force('center', forceCenter(width / 2, height / 2))
      .force(
        'collide',
        forceCollide<SimNode>((d) => radius(d) + 6),
      );
    simulationRef.current = simulation;

    const link = linkLayer
      .selectAll<SVGLineElement, SimLink>('line')
      .data(links)
      .join('line')
      .attr('stroke', (d) => ROLE_COLOR[d.roleCategory] ?? 'var(--line)')
      .attr('stroke-width', 1.6)
      .attr('stroke-dasharray', (d) => (d.historical ? '4 3' : null))
      .attr('opacity', (d) => (d.historical ? 0.5 : 0.85));

    const node = nodeLayer
      .selectAll<SVGGElement, SimNode>('g')
      .data(nodes)
      .join('g')
      .style('cursor', 'pointer')
      .on('click', (_event, d) => onSelectNode?.(d.id));
    nodeSelRef.current = node;

    node
      .append('circle')
      .attr('r', radius)
      .attr('fill', (d) => (d.type === 'company' ? 'var(--brand)' : 'var(--bg-raised)'))
      .attr('stroke', (d) =>
        isHub(d) ? 'var(--risk-mid)' : d.type === 'company' ? 'var(--brand-ink)' : 'var(--slate)',
      )
      .attr('stroke-width', (d) => (isHub(d) ? 3 : 1.5))
      .attr('stroke-dasharray', (d) => (isUnconfirmed(d) ? '3 2' : null));

    // Zámok pozície (2026-07-20, Foaf.sk vzor z prieskumu konkurencie) — malý bodkovaný
    // prstenec navyše okolo nodu, ktorý user niekedy potiahol myšou (d.fx/d.fy nastavené
    // v drag handleri nižšie a NEnulované na "end", na rozdiel od predošlého správania).
    // Vizuálne odlíšené od `isHub`/`isUnconfirmed` prstenca vlastnou triedou.
    node
      .append('circle')
      .attr('class', 'pin-ring')
      .attr('r', (d) => radius(d) + 5)
      .attr('fill', 'none')
      .attr('stroke', 'var(--brand-2)')
      .attr('stroke-width', 1.3)
      .attr('stroke-dasharray', '2 2')
      .style('display', (d) => (d.fx != null ? null : 'none'));

    node.append('title').text((d) => {
      if (isUnconfirmed(d)) {
        return `${d.label} — nepotvrdená identita: v zdrojových dátach chýba dátum narodenia, takže toto môže byť iná osoba s rovnakým menom. Graf preto ďalej nerozvetvuje k iným firmám tejto osoby.`;
      }
      return d.type === 'person' && d.degree > 1
        ? `${d.label} — pôsobí v ${d.degree} väzbách v tomto grafe`
        : d.label;
    });

    node
      .append('text')
      .text((d) => (isUnconfirmed(d) ? `${d.label} ?` : d.label))
      .attr('x', (d) => radius(d) + 4)
      .attr('y', 4)
      .attr('font-size', 11)
      .attr('font-style', (d) => (isUnconfirmed(d) ? 'italic' : null))
      .attr('font-weight', (d) => (isHub(d) ? 700 : 400))
      .attr('font-family', 'var(--sans)')
      .attr('fill', (d) =>
        isHub(d) ? 'var(--risk-mid)' : isUnconfirmed(d) ? 'var(--slate)' : 'var(--ink)',
      );

    // Ťahanie DRŽÍ pozíciu po pustení (d.fx/d.fy sa už na "end" NENulujú) — predtým sa
    // node po ťahaní vrátil pod vplyv simulácie, čo pôsobilo, že ťahanie "nedrží". Uzol
    // sa dá odomknúť len cez tlačidlo "Odomknúť polohy" v toolbar-e nižšie (nie klikom,
    // ten už navigáciu na profil, viď onSelectNode vyššie).
    let dragMoved = false;
    node.call(
      drag<SVGGElement, SimNode>()
        .on('start', (event, d) => {
          dragMoved = false;
          if (!event.active) simulation.alphaTarget(0.3).restart();
          d.fx = d.x;
          d.fy = d.y;
        })
        .on('drag', (event, d) => {
          dragMoved = true;
          d.fx = event.x;
          d.fy = event.y;
        })
        .on('end', function (event, d) {
          if (!event.active) simulation.alphaTarget(0);
          if (!dragMoved) {
            // Klik bez pohybu — nebol to zámer uzamknúť, uvoľni naspäť.
            d.fx = null;
            d.fy = null;
          }
          // Aktualizuj prstenec hneď (nezávisle od toho, či simulácia ešte tiká —
          // `this` je tu vždy pôvodný <g> uzlu vďaka funkčnej, nie šípkovej, syntaxi
          // v .on() handleri, na rozdiel od nespoľahlivého event.sourceEvent.currentTarget).
          const pinRing = select(this).select('circle.pin-ring');
          if (d.fx != null) pinRing.style('display', null);
          else pinRing.style('display', 'none');
        }),
    );

    simulation.on('tick', () => {
      link
        .attr('x1', (d) => (d.source as SimNode).x ?? 0)
        .attr('y1', (d) => (d.source as SimNode).y ?? 0)
        .attr('x2', (d) => (d.target as SimNode).x ?? 0)
        .attr('y2', (d) => (d.target as SimNode).y ?? 0);
      node.attr('transform', (d) => `translate(${d.x ?? 0},${d.y ?? 0})`);
      // Zámok-prstenec (pin-ring) sa prekresľuje z `d.fx` priamo tu, nie v drag
      // 'end' handleri — `event.sourceEvent.currentTarget` tam nie je spoľahlivo
      // vždy pôvodný <g> nodu (mouseup často bubluje z window/document počas
      // aktívneho ťahania), takže odvodenie z dát na každom ticku je jednoduchšie
      // aj spoľahlivejšie.
      node.select('circle.pin-ring').style('display', (d) => (d.fx != null ? null : 'none'));
    });

    const zoomBehavior = zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.3, 3])
      .on('zoom', (event) => viewport.attr('transform', event.transform.toString()));
    svg.call(zoomBehavior);
    svg.call(zoomBehavior.transform, zoomIdentity);
    zoomRef.current = zoomBehavior;

    return () => {
      simulation.stop();
    };
  }, [nodes, links, onSelectNode]);

  function zoomBy(factor: number): void {
    if (!svgRef.current || !zoomRef.current) return;
    zoomRef.current.scaleBy(select(svgRef.current), factor);
  }

  function resetView(): void {
    if (!svgRef.current || !zoomRef.current) return;
    zoomRef.current.transform(select(svgRef.current), zoomIdentity);
  }

  function unlockAll(): void {
    for (const n of nodes) {
      n.fx = null;
      n.fy = null;
    }
    nodeSelRef.current?.select('circle.pin-ring').style('display', 'none');
    simulationRef.current?.alphaTarget(0.3).restart();
    setTimeout(() => simulationRef.current?.alphaTarget(0), 300);
  }

  function toggleFullscreen(): void {
    const el = containerRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      void el.requestFullscreen().then(() => setIsFullscreen(true));
    } else {
      void document.exitFullscreen().then(() => setIsFullscreen(false));
    }
  }

  function exportSvg(): void {
    const svgEl = svgRef.current;
    if (!svgEl) return;
    const serialized = new XMLSerializer().serializeToString(svgEl);
    const blob = new Blob([`<?xml version="1.0" encoding="UTF-8"?>\n${serialized}`], {
      type: 'image/svg+xml',
    });
    downloadBlob(blob, 'graf-prepojeni.svg');
  }

  function exportPng(): void {
    const svgEl = svgRef.current;
    if (!svgEl) return;
    const width = svgEl.clientWidth || 800;
    const height = HEIGHT;
    const serialized = new XMLSerializer().serializeToString(svgEl);
    const svgBlob = new Blob([serialized], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(svgBlob);
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = width * 2;
      canvas.height = height * 2;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.scale(2, 2);
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob((blob) => {
          if (blob) downloadBlob(blob, 'graf-prepojeni.png');
        });
      }
      URL.revokeObjectURL(url);
    };
    img.src = url;
  }

  return (
    <div
      ref={containerRef}
      style={{ width: '100%', background: isFullscreen ? 'var(--bg)' : undefined }}
    >
      <div className="graph-toolbar">
        <button type="button" onClick={() => zoomBy(1.3)} title="Priblížiť">
          +
        </button>
        <button type="button" onClick={() => zoomBy(1 / 1.3)} title="Oddialiť">
          −
        </button>
        <button type="button" onClick={resetView} title="Vrátiť pôvodný pohľad">
          ⟲
        </button>
        <button type="button" onClick={unlockAll} title="Odomknúť všetky uzamknuté polohy uzlov">
          Odomknúť polohy
        </button>
        <span className="graph-toolbar-sep" />
        <button type="button" onClick={toggleFullscreen} title="Celá obrazovka">
          {isFullscreen ? 'Zavrieť celú obrazovku' : 'Celá obrazovka'}
        </button>
        <button type="button" onClick={exportSvg} title="Stiahnuť ako SVG">
          SVG ↓
        </button>
        <button type="button" onClick={exportPng} title="Stiahnuť ako PNG">
          PNG ↓
        </button>
      </div>
      <svg ref={svgRef} width="100%" height={HEIGHT} style={{ display: 'block' }} />
    </div>
  );
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
